import { useEffect, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { Quote } from './domain'

/** Load state for the Quote fetch this hook owns. */
export type QuoteLoadState = 'loading' | 'loaded' | 'error'

/**
 * UID-134 (originally `RfqCard.tsx`'s private `useCardQuote`) — fetches the
 * Quote once for a quoted/accepted RFQ. Extracted verbatim for UID-135
 * (ADR-0056 Decision 2/4) so `RfqCard`, the new compact `RfqListRow`, and the
 * new `RfqDetailSummaryRail` all share ONE fetch implementation rather than
 * each re-deriving the same 401/error/loaded semantics — a pure,
 * behavior-preserving move (identical semantics: a 401 leaves the caller in
 * `loading` forever, handled globally by the session-invalidation
 * middleware; any other error surfaces `error`; a successful body is
 * `loaded`). Disabled (never fetches) when `enabled` is false.
 */
export function useRfqQuote(
  rfqId: string,
  enabled: boolean,
): { quote: Quote | undefined; quoteLoadState: QuoteLoadState } {
  const [quote, setQuote] = useState<Quote | undefined>(undefined)
  const [quoteLoadState, setQuoteLoadState] = useState<QuoteLoadState>('loading')

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    async function load() {
      const { data, error } = await apiClient.GET('/rfq/{rfqId}/quote', {
        params: { path: { rfqId } },
      })
      if (cancelled) return
      if (data) {
        setQuote(data)
        setQuoteLoadState('loaded')
        return
      }
      if (error && (error as { status?: number }).status !== 401) {
        setQuoteLoadState('error')
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [rfqId, enabled])

  return { quote, quoteLoadState }
}
